import { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Trans, useTranslation } from 'react-i18next';
import { Eye, EyeOff, Mail, AlertCircle, Lock, RotateCcw } from 'lucide-react';
import Button from '../components/common/Button';
import Input from '../components/common/Input';
import { register, resendRestoreLink } from '../services/auth';
import { getPublicConfig, validateInvitationToken } from '../services/admin';
import { getErrorMessage } from '../services/api';
import OptionToggle from '../components/common/OptionToggle';
import SegmentedControl from '../components/common/SegmentedControl';

export default function Register() {
  const { t } = useTranslation('auth');
  const [searchParams] = useSearchParams();
  const invitationToken = searchParams.get('invitation') || undefined;
  const joinEmail = searchParams.get('join') || undefined;
  const prelaunchToken = searchParams.get('invitation_token') || undefined;

  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    firstName: '',
    lastName: '',
    referralSource: '',
    referralSourceOther: '',
  });
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isCheckingAccess, setIsCheckingAccess] = useState(true);
  const [error, setError] = useState('');
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [registrationComplete, setRegistrationComplete] = useState(false);
  const [registeredEmail, setRegisteredEmail] = useState('');
  // L'adresse appartient à un compte supprimé encore récupérable : on propose la
  // récupération plutôt que de renvoyer « adresse déjà utilisée »
  const [deletedAccountDays, setDeletedAccountDays] = useState<number | null>(null);
  const [isDeletedAccount, setIsDeletedAccount] = useState(false);
  const [isResendingLink, setIsResendingLink] = useState(false);
  const [restoreLinkSent, setRestoreLinkSent] = useState(false);

  // État pour la configuration et le token d'invitation
  const [registrationEnabled, setRegistrationEnabled] = useState(true);
  const [hasValidPrelaunchToken, setHasValidPrelaunchToken] = useState(false);
  const [prelaunchEmail, setPrelaunchEmail] = useState<string | null>(null);
  const [tokenError, setTokenError] = useState<string | null>(null);

  const navigate = useNavigate();

  // Vérifier la configuration et le token au chargement
  useEffect(() => {
    const checkAccess = async () => {
      setIsCheckingAccess(true);
      try {
        // Si on a un token d'invitation pré-lancement, le valider d'abord
        // (indépendamment du statut des inscriptions)
        if (prelaunchToken) {
          try {
            const tokenResponse = await validateInvitationToken(prelaunchToken);
            if (tokenResponse.success && tokenResponse.data?.email) {
              setHasValidPrelaunchToken(true);
              setPrelaunchEmail(tokenResponse.data.email);
              setFormData(prev => ({ ...prev, email: tokenResponse.data!.email }));
              // Token valide : on peut afficher le formulaire même si inscriptions fermées
              setRegistrationEnabled(true);
              setIsCheckingAccess(false);
              return;
            }
          } catch (tokenErr) {
            // Erreur de validation du token (expiré, invalide, etc.)
            console.error('Token validation error:', tokenErr);
            // Récupérer le message d'erreur de l'API si disponible
            const axiosError = tokenErr as { response?: { data?: { message?: string; expired?: boolean } } };
            if (axiosError.response?.data?.expired) {
              setTokenError(t('registrationClosed.expiredInvitation'));
            } else if (axiosError.response?.data?.message) {
              setTokenError(axiosError.response.data.message);
            } else {
              setTokenError(t('registrationClosed.invalidInvitation'));
            }
          }
        }

        // Vérifier si les inscriptions publiques sont activées
        const configResponse = await getPublicConfig();
        const isEnabled = configResponse.success && configResponse.data?.registrationEnabled;
        setRegistrationEnabled(!!isEnabled);
      } catch (err) {
        console.error('Error checking access:', err);
        // En cas d'erreur réseau sur getPublicConfig, on autorise l'accès par défaut
        setRegistrationEnabled(true);
      } finally {
        setIsCheckingAccess(false);
      }
    };

    checkAccess();
  }, [prelaunchToken]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleResendRestoreLink = async () => {
    setIsResendingLink(true);
    try {
      await resendRestoreLink(formData.email);
      setRestoreLinkSent(true);
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setIsResendingLink(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsDeletedAccount(false);
    setRestoreLinkSent(false);

    if (formData.password !== formData.confirmPassword) {
      setError(t('register.passwordsMismatch'));
      return;
    }

    if (!acceptTerms) {
      setError(t('register.mustAcceptTerms'));
      return;
    }

    setIsLoading(true);

    try {
      const response = await register({
        email: formData.email,
        password: formData.password,
        firstName: formData.firstName || undefined,
        lastName: formData.lastName || undefined,
        invitationToken,
        joinEmail,
        prelaunchToken: prelaunchToken || undefined,
        referralSource: formData.referralSource || undefined,
        referralSourceOther: formData.referralSource === 'other' ? formData.referralSourceOther || undefined : undefined,
      });

      if (response.success && response.data) {
        setRegisteredEmail(formData.email);
        setRegistrationComplete(true);
      }
    } catch (err) {
      const apiError = err as {
        response?: { data?: { code?: string; data?: { daysRemaining?: number } } };
      };

      if (apiError.response?.data?.code === 'ACCOUNT_DELETED_RESTORABLE') {
        setIsDeletedAccount(true);
        setDeletedAccountDays(apiError.response.data.data?.daysRemaining ?? null);
      } else {
        setError(getErrorMessage(err));
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Écran de chargement
  if (isCheckingAccess) {
    return (
      <div className="pub-page min-h-screen flex items-center justify-center px-4 py-12">
        <div className="text-center">
          <div className="animate-spin w-8 h-8 border-4 border-primary-600 border-t-transparent rounded-full mx-auto mb-4" />
          <p className="pub-muted">{t('register.checking')}</p>
        </div>
      </div>
    );
  }

  // Écran inscriptions désactivées
  if (!registrationEnabled && !hasValidPrelaunchToken) {
    return (
      <div className="pub-page min-h-screen flex items-center justify-center px-4 py-12">
        <div className="max-w-md w-full">
          <div className="text-center mb-8">
            <Link to="/" className="text-3xl font-bold text-primary-600">
              Splinity
            </Link>
          </div>

          <div className="pub-card p-8 text-center">
            <div className="w-16 h-16 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <Lock className="w-8 h-8 text-yellow-600" />
            </div>

            <h1 className="pub-title text-2xl font-bold mb-4">
              {t('registrationClosed.title')}
            </h1>

            {tokenError ? (
              <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm flex items-center gap-2">
                <AlertCircle className="w-5 h-5 flex-shrink-0" />
                {tokenError}
              </div>
            ) : (
              <p className="pub-muted mb-6">
                {t('registrationClosed.message')}
              </p>
            )}

            <div className="space-y-3">
              <Link to="/">
                <Button variant="primary" className="w-full">
                  {t('registrationClosed.requestAccess')}
                </Button>
              </Link>
              <Link to="/login">
                <Button variant="outline" className="w-full">
                  {t('registrationClosed.hasAccount')}
                </Button>
              </Link>
            </div>
          </div>

          <p className="pub-subtle mt-8 text-center text-sm">
            <Link to="/" className="hover:text-primary-600">
              &larr; {t('backToHome', { ns: 'common' })}
            </Link>
          </p>
        </div>
      </div>
    );
  }

  // Écran de confirmation d'envoi d'email
  if (registrationComplete) {
    return (
      <div className="pub-page min-h-screen flex items-center justify-center px-4 py-12">
        <div className="max-w-md w-full">
          <div className="text-center mb-8">
            <Link to="/" className="text-3xl font-bold text-primary-600">
              Splinity
            </Link>
          </div>

          <div className="pub-card p-8 text-center">
            <div className="pub-ico w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-6">
              <Mail className="w-8 h-8 text-primary-600" />
            </div>

            <h1 className="pub-title text-2xl font-bold mb-4">
              {t('verifyEmail.checkTitle')}
            </h1>

            <p className="pub-muted mb-6">
              {t('verifyEmail.emailSent')}{' '}
              <strong>{registeredEmail}</strong>.
              {' '}{t('verifyEmail.clickLink')}
            </p>

            <div className="pub-notice rounded-lg p-4 mb-6">
              <p className="pub-subtle text-sm">
                {t('verifyEmail.notReceived')}{' '}
                <Link
                  to={`/verify-email?email=${encodeURIComponent(registeredEmail)}`}
                  className="text-primary-600 hover:underline"
                >
                  {t('verifyEmail.requestNew')}
                </Link>
                .
              </p>
            </div>

            <Button
              variant="outline"
              className="w-full"
              onClick={() => navigate('/login')}
            >
              {t('backToLogin', { ns: 'common' })}
            </Button>
          </div>

          <p className="pub-subtle mt-8 text-center text-sm">
            <Link to="/" className="hover:text-primary-600">
              &larr; {t('backToHome', { ns: 'common' })}
            </Link>
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="pub-page min-h-screen flex items-center justify-center px-4 py-12">
      <div className="max-w-md w-full">
        <div className="text-center mb-8">
          <Link to="/" className="text-3xl font-bold text-primary-600">
            Splinity
          </Link>
          <h1 className="pub-title mt-6 text-2xl font-bold">
            {t('register.title')}
          </h1>
          <p className="pub-muted mt-2">
            {t('register.hasAccount')}{' '}
            <Link to="/login" className="text-primary-600 hover:underline">
              {t('register.signIn')}
            </Link>
          </p>
        </div>

        {/* Message si invitation pré-lancement */}
        {hasValidPrelaunchToken && prelaunchEmail && (
          <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-lg text-green-700 text-sm">
            {t('register.prelaunchMessage')}
          </div>
        )}

        {/* Message si invitation partenaire */}
        {invitationToken && (
          <div className="pub-notice pub-notice--accent mb-4 p-4 rounded-lg text-sm">
            {t('register.partnerInviteMessage')}
          </div>
        )}

        {/* Message si demande de jonction */}
        {joinEmail && (
          <div className="pub-notice pub-notice--accent mb-4 p-4 rounded-lg text-sm">
            <Trans t={t} i18nKey="register.joinMessage" values={{ email: joinEmail }} components={{ strong: <strong /> }} />
          </div>

        )}

        <div className="pub-card p-8">
          {error && (
            <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
              {error}
            </div>
          )}

          {isDeletedAccount && (
            <div className="mb-4 p-4 bg-amber-50 border border-amber-200 rounded-lg text-amber-900 text-sm">
              <p className="font-semibold mb-1">{t('register.deletedAccount.title')}</p>
              <p className="mb-3">
                {deletedAccountDays !== null
                  ? t('register.deletedAccount.messageWithDelay', { count: deletedAccountDays })
                  : t('register.deletedAccount.message')}
              </p>
              <div className="flex flex-wrap gap-2">
                <Link to="/login">
                  <Button type="button" variant="primary" size="sm">
                    {t('register.deletedAccount.signIn')}
                  </Button>
                </Link>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  leftIcon={<RotateCcw className="w-4 h-4" />}
                  isLoading={isResendingLink}
                  disabled={restoreLinkSent}
                  onClick={handleResendRestoreLink}
                >
                  {restoreLinkSent
                    ? t('register.deletedAccount.linkSent')
                    : t('register.deletedAccount.resendLink')}
                </Button>
              </div>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-5">
            <Input
              label={t('register.email')}
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              placeholder={t('register.emailPlaceholder')}
              required
              autoComplete="email"
              disabled={!!prelaunchEmail}
              helperText={prelaunchEmail ? t('register.emailLinkedToInvitation') : undefined}
            />

            <div className="grid grid-cols-2 gap-4">
              <Input
                label={t('register.firstName')}
                type="text"
                name="firstName"
                value={formData.firstName}
                onChange={handleChange}
                placeholder={t('register.firstNamePlaceholder')}
                autoComplete="given-name"
              />
              <Input
                label={t('register.lastName')}
                type="text"
                name="lastName"
                value={formData.lastName}
                onChange={handleChange}
                placeholder={t('register.lastNamePlaceholder')}
                autoComplete="family-name"
              />
            </div>

            <div className="relative">
              <Input
                label={t('register.password')}
                type={showPassword ? 'text' : 'password'}
                name="password"
                value={formData.password}
                onChange={handleChange}
                placeholder="••••••••"
                required
                autoComplete="new-password"
                helperText={t('validation.passwordRequirements', { ns: 'common' })}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="pub-subtle absolute right-3 top-8 hover:text-primary-600 transition-colors"
              >
                {showPassword ? (
                  <EyeOff className="w-5 h-5" />
                ) : (
                  <Eye className="w-5 h-5" />
                )}
              </button>
            </div>

            <Input
              label={t('register.confirmPassword')}
              type={showPassword ? 'text' : 'password'}
              name="confirmPassword"
              value={formData.confirmPassword}
              onChange={handleChange}
              placeholder="••••••••"
              required
              autoComplete="new-password"
            />

            {/* Comment nous avez-vous connus ? */}
            <div>
              <label className="block text-sm font-medium pub-muted mb-2">
                {t('register.referralTitle')}
                <span className="pub-subtle font-normal ml-1">{t('optional', { ns: 'common' })}</span>
              </label>
              <SegmentedControl
                wrap
                ariaLabel={t('register.referralTitle')}
                value={formData.referralSource}
                onChange={(value) => setFormData({ ...formData, referralSource: value, referralSourceOther: value !== 'other' ? '' : formData.referralSourceOther })}
                options={[
                  { value: 'google', label: t('register.referralGoogle') },
                  { value: 'discord', label: t('register.referralDiscord') },
                  { value: 'word_of_mouth', label: t('register.referralWordOfMouth') },
                  { value: 'social_media', label: t('register.referralSocialMedia') },
                  { value: 'youtube', label: t('register.referralYoutube') },
                  { value: 'friend', label: t('register.referralFriend') },
                  { value: 'other', label: t('register.referralOther') },
                ]}
              />
              {formData.referralSource === 'other' && (
                <input
                  type="text"
                  name="referralSourceOther"
                  value={formData.referralSourceOther}
                  onChange={(e) => setFormData({ ...formData, referralSourceOther: e.target.value })}
                  placeholder={t('register.referralOtherPlaceholder')}
                  className="pub-field mt-2 w-full px-3 py-2 rounded-lg text-sm focus:ring-primary-500 focus:border-primary-500"
                  maxLength={100}
                />
              )}
            </div>

            {/* Le libellé contient des liens : ils doivent rester HORS du bouton
                (un <a> dans un <button> est invalide et rend le lien inactivable). */}
            <div className="flex items-start gap-3">
              <OptionToggle
                variant="box"
                selected={acceptTerms}
                onToggle={() => setAcceptTerms(!acceptTerms)}
                className="mt-0.5"
              >
                {t('register.acceptTerms')}
              </OptionToggle>
              <span className="pub-muted text-sm">
                {t('register.acceptTerms')}{' '}
                <Link to="/conditions-utilisation" className="text-primary-600 hover:underline">
                  {t('register.termsOfUse')}
                </Link>{' '}
                {t('register.and')}{' '}
                <Link to="/politique-confidentialite" className="text-primary-600 hover:underline">
                  {t('register.privacyPolicy')}
                </Link>
              </span>
            </div>

            <Button
              type="submit"
              variant="primary"
              className="w-full"
              size="lg"
              isLoading={isLoading}
            >
              {t('register.submit')}
            </Button>
          </form>
        </div>

        <p className="pub-subtle mt-8 text-center text-sm">
          <Link to="/" className="hover:text-primary-600">
            &larr; {t('backToHome', { ns: 'common' })}
          </Link>
        </p>
      </div>
    </div>
  );
}
